import type {TreeNode, TreeNodeWithChildren} from './types';
import {isNullOrUndefined} from 'shared/utils/custom-utils';
import type {Childrenable} from '../types';

export function isNodeExpanded<ID>(expandedNodes: TreeNode<ID>[], node: TreeNode<ID>): boolean {
    return expandedNodes.some((expandedNode) => treeNodesEquals(expandedNode, node));
}

export function isNodeChecked<ID>(checkedNodesIds: ID[], node: TreeNode<ID>): boolean {
    return checkedNodesIds.some((selectedNodeId) => node.id === selectedNodeId);
}

export function isAnySubtreeNodeChecked<ID>(checkedNodesIds: ID[], node: TreeNodeWithChildren<ID>): boolean {
    if (checkedNodesIds.includes(node.id)) {
        return true;
    }
    return node.children.some((childNode) => isAnySubtreeNodeChecked(checkedNodesIds, childNode));
}

export function flattenTree<ID>(subTree: TreeNodeWithChildren<ID>): TreeNode<ID>[] {
    return [subTree, ...subTree.children.flatMap(flattenTree)]
}

export function findParentNodeInSubtree<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>): TreeNodeWithChildren<ID> | undefined {
    if (subtree.children.some((childNode) => treeNodesEquals(childNode, node))) {
        return subtree;
    }

    for (const childNode of subtree.children) {
        const foundParent = findParentNodeInSubtree(childNode, node);
        if (!isNullOrUndefined(foundParent)) {
            return foundParent;
        }
    }

    return undefined;
}

export function findNextVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[visibleNodes.findIndex((visibleNode) => treeNodesEquals(visibleNode, node)) + 1];
}

export function findPreviousVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[visibleNodes.findIndex((visibleNode) => treeNodesEquals(visibleNode, node)) - 1];
}

export function findLastVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[visibleNodes.length - 1];
}

export function findFirstVisibleNode<ID>(subtree: TreeNodeWithChildren<ID>, node: TreeNode<ID>, expandedNodes: TreeNode<ID>[]): TreeNode<ID> {
    const visibleNodes = getVisibleNodesInPreorder(subtree, expandedNodes);
    return visibleNodes[0];
}

// preorder tree traversal (in order they are displayed to a user)
function getVisibleNodesInPreorder<ID>(tree: TreeNodeWithChildren<ID>, expanded: TreeNode<ID>[]): TreeNode<ID>[] {
    if (!isNodeExpanded(expanded, tree)) {
        return [tree]; // ignore children of collapsed nodes
    } else {
        return [tree, ...tree.children.flatMap((child) => getVisibleNodesInPreorder(child, expanded))];
    }
}

export function treeNodesEquals<ID>(node1: TreeNode<ID>, node2: TreeNode<ID>): boolean {
    return node1.id === node2.id;
}

export function createTreeNode<VALUE, ID>(value: Childrenable<VALUE>, disabledItems: Childrenable<VALUE>[], isDisabled: boolean, idResolver: (_: Childrenable<VALUE>) => ID): TreeNodeWithChildren<ID> {
    const id = idResolver(value);
    const disabled = isDisabled || disabledItems.some((disableValue) => idResolver(disableValue) === idResolver(value));
    const children = !value.children ? [] : value.children.map((child) => createTreeNode(child, disabledItems, isDisabled, idResolver));

    return {id, disabled, children};
}